/**
 * Classification core enums (ADR-0007).
 * Dimensions and attributes are DATA, not schema — these enums type the small
 * fixed sets that DO live in code (the kind of an axis, the shape of its values,
 * and the external systems a value can map to).
 */

/** An axis is either a classification dimension or a free-value attribute. */
export enum AxisKind {
	DIMENSION = 'dimension',
	ATTRIBUTE = 'attribute',
}

/**
 * The axis codes the workbook declares for the merck tenant. Axes are DATA (rows),
 * so this enum is a typed reference to the codes the engine and nav paths use by
 * name; it is NOT the source of truth (the `axis` table is, loaded from
 * `ATLAS-Taxonomy.xlsx`) and adding an axis stays an INSERT.
 *
 * A member here MUST name an axis the workbook declares. One that does not is
 * worse than a string literal: it typechecks, so every caller keeps compiling
 * while silently reading an axis that resolves to nothing.
 */
export enum AxisCode {
	// Dimensions
	SPECIES = 'species',
	// Brand AND product: two levels of one axis, told apart by depth.
	BRAND = 'brand',
	PRODUCT_INDICATION = 'product_indication',
	AUDIENCE = 'audience',
	LOCALE = 'locale',
	GEOGRAPHY = 'geography',
	CHANNEL = 'channel',
	// Attributes
	CAMPAIGN = 'campaign',
	BUSINESS_UNIT = 'business_unit',
	PRODUCT_FAMILY = 'product_family',
	ASPECT_RATIO = 'aspect_ratio',
	LICENSE = 'license',
	// Work-order axes (seed migration 0011)
	COMPLEXITY_LEVEL = 'complexity_level',
	ASSET_TYPE_CODE = 'asset_type_code',
	DURATION_SECONDS = 'duration_seconds',
	ORDER_CODE = 'order_code',
	// The two Veeva codes an order carries: what it is DERIVED from, and what it is
	// produced FOR. The workbook names them for which end they describe rather than
	// leaving the target one unqualified.
	VEEVA_CODE = 'target_veeva_code',
	ORIGINAL_VEEVA_CODE = 'source_veeva_code',
	FILE_TYPE = 'file_type',
	// The four scene axes and `visual_brief` were RETIRED by migration 0044. They were
	// classification in name only: nothing ever queried them, and their sole consumer
	// turned them into art direction for one image prompt. That is prompt-library data
	// — an option a generator offers is content the prompt owns, while an axis is what
	// an object IS — so the same vocabulary now lives as prompt lists, and the scene is
	// composed by the generation-context prompt's own variables.
	//
	// `species` deliberately stayed: it is real classification, set on the order and
	// read by file resolution and the brief summary. The prompt list mirroring it is a
	// copy for the picker, with a drift guard, not a replacement.
	/** `<template key>@<version>`, identifying which family a stored spec describes. */
	TEMPLATE_KEY = 'template_key',
	// Source / deliverable classification (seed migration 0019)
	DELIVERABLE_TYPE = 'deliverable_type',
	SOURCE_TYPE = 'source_type',
	DELIVERABLE_ORDER = 'deliverable_order', // attribute (number): per-file order of final_visual deliverables
	// Campaign axes (taxonomy seed migration)
	OBJECTIVE = 'objective',
	CAMPAIGN_CODE = 'campaign_code',
}

/**
 * `file_type` dimension values referenced in code. Every value here is seeded as an
 * `axis_value`; values that no code path names stay data-only.
 *
 * Work-order roles: `deliverable` = a produced final; `source` = a base/input file to
 * work from (renamed from `original_editable` in 0019); `seed_messages_excel` = the
 * message-copy seed spreadsheet uploaded on an order (a SEMANTIC role — what the file
 * is FOR — not the `.xlsx` extension). The rest are brand-kit roles: the reusable
 * assets a brand supplies, as opposed to anything produced for an order.
 */
export enum FileType {
	DELIVERABLE = 'deliverable',
	SOURCE = 'source',
	SEED_MESSAGES_EXCEL = 'seed_messages_excel',
	LOGO = 'logo',
	PRODUCT_LOGO = 'product_logo',
	PRODUCT_PACKAGING = 'product_packaging',
	ICONS = 'icons',
	FONT = 'font',
	BRANDBOOK = 'brandbook',
	BACKGROUND = 'background',
	GRAPHIC_ELEMENT = 'graphic_element',
	/** Seals and stamps a brand supplies — e.g. a protection or duration seal placed on artwork. */
	STAMP = 'stamp',
	/** Stock imagery and local shooting photography a brand supplies for reuse. */
	STOCK_IMAGE = 'stock_image',
	/** A template family's zone contract, stored as a classified JSON document. */
	TEMPLATE_SPEC = 'template_spec',
	/** A file the designer works from that is not the order's source material: a mood, a layout, a past piece. */
	REFERENCE_ASSET = 'reference_asset',
	/** A campaign's guidelines, adapted to one market. */
	LOCAL_CAMPAIGN_GUIDELINES = 'local_campaign_guidelines',
	/** A campaign's guidelines, as the global team issues them. */
	GLOBAL_CAMPAIGN_GUIDELINES = 'global_campaign_guidelines',
	/** Where and when a campaign's pieces run. */
	MEDIA_PLAN = 'media_plan',
	/** The copy an order's piece carries, as the client sent it (Word, PDF or Excel). */
	COPY_DOCUMENT = 'copy_document',
	/** How an order's piece must be produced, as the PM attaches it for the designer. */
	PRODUCTION_INSTRUCTIONS = 'production_instructions',
}

/**
 * The `file_type` values that make up a brand kit — the reusable assets a brand
 * supplies. Deliberately excludes the work-order roles (`deliverable`, `source`,
 * `seed_messages_excel`) and the `other` catch-all, so a brand-kit read cannot pick
 * up files produced for an order.
 *
 * The ORDER is the reading order every kit listing presents these roles in, brandbook
 * first: it is the document the rest of the kit is governed by, so it leads the shelf.
 */
export const BRAND_KIT_FILE_TYPES: readonly FileType[] = [
	FileType.BRANDBOOK,
	FileType.LOGO,
	FileType.PRODUCT_LOGO,
	FileType.PRODUCT_PACKAGING,
	FileType.ICONS,
	FileType.FONT,
	FileType.BACKGROUND,
	FileType.GRAPHIC_ELEMENT,
	FileType.STAMP,
	FileType.STOCK_IMAGE,
] as const

/**
 * `deliverable_type` values — sub-classify a `file_type=deliverable` file.
 *
 * The CODES are the workbook's, which renamed two of them: what the code still calls
 * `FINAL_VISUAL` is `visual_export`, and `VALIDATION` is `validation_file`. The member
 * names are kept so the many call sites reading `DeliverableType.FINAL_VISUAL` — the
 * validation gate, message import, the deliverables grid — keep meaning what they meant.
 *
 * A member here MUST name a value the workbook declares. One that does not still
 * typechecks, so an upload tagging it is only rejected at the API, by which point the
 * file is already stored.
 */
export enum DeliverableType {
	EDITABLE = 'editable',
	FINAL_VISUAL = 'visual_export',
	VALIDATION = 'validation_file',
	DISTRIBUTION_PACKAGE = 'distribution_package',
	MOCKUP = 'mockup',
}

/** `source_type` values — sub-classify a `file_type=source` file (0019). */
export enum SourceType {
	EDITABLE = 'editable',
	/** The client's distribution package an order is made from, when it is not in Veeva. */
	ORIGINAL_DISTRIBUTION_PACKAGE = 'original_distribution_package',
	/** An image attached to a work-order comment to show what its author means. */
	REFERENCE = 'reference',
}

/**
 * Object types hosted by the polymorphic classification anchor. Promoted domain
 * objects (brand/product) join file/rule/prompt as first-class classifiable
 * types.
 */
export enum ClassifiedObjectType {
	FILE = 'file',
	RULE = 'rule',
	PROMPT = 'prompt',
	BRAND = 'brand',
	PRODUCT = 'product',
	WORK_ORDER = 'work_order',
	MESSAGE = 'message',
	CAMPAIGN = 'campaign',
	ARTICLE = 'article',
}

/** How an axis's values are shaped. `list` = picked from `axis_value` rows. */
export enum ValueType {
	LIST = 'list',
	TEXT = 'text',
	URL = 'url',
	DATE = 'date',
	NUMBER = 'number',
}

/**
 * External systems a canonical value can be mapped to (one value, many names).
 *
 * `TAXONOMY_TITLE` is the DISPLAY TITLE the taxonomy gives the value in a market - the
 * name a human reads. `VEEVA` and `WORKFRONT` are integration identifiers (a SKU, a
 * project key). The split matters to any consumer resolving a NAME: it must consider
 * title rows only, or an integration code surfaces as an object's name.
 */
export enum MappingSystem {
	TAXONOMY_TITLE = 'taxonomy_title',
	VEEVA = 'veeva',
	WORKFRONT = 'workfront',
}

/**
 * Which write path is applying a classification tuple. An empty tuple is
 * ambiguous on its own — "the caller submitted none" on a create (the schema
 * defaults it to `{}`) versus "clear every dimension" on a partial update —
 * so the caller states which it means rather than the writer guessing.
 */
export enum ClassificationWriteMode {
	CREATE = 'create',
	UPDATE = 'update',
}

/** Coverage-matching mode (ADR-0007). */
export enum MatchMode {
	/** Every axis the object constrains must be covered by the context. */
	STRICT = 'strict',
	/** Only the axes present in the context are checked. */
	LENIENT = 'lenient',
}

/** What an external code resolved to on one axis (`resolveExternalCodes`). */
export enum ExternalCodeMatch {
	EXACT = 'exact',
	MISSING = 'missing',
	/** More than one active value of the axis carries the code in the winning scope. */
	AMBIGUOUS = 'ambiguous',
}
